import {
	Avatar,
	Box,
	Card,
	Checkbox,
	Grid,
	ListItem,
	ListItemText,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import bankCardStyle from './OtherFIUCard.styles';
import sizeFontCustom from '../Discovery/fontSize.styles';
import { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { getFipDisplayName, getFipLogoAlt } from '../../utils/fipLogo.utils';
import { handleActivationKey, keyboardFocusVisibleSx } from '../../utils/a11y.utils';

function OtherFIUCard({
	xs,
	md,
	lg,
	bankObject,
	handleOtherFipCheckboxChange,
	checkedBanksList,
	boxShadow,
	height,
	customDetails,
}) {
	const theme = useTheme();
	const { t } = useTranslation();
	const [isTooltipOpen, setIsTooltipOpen] = useState(false);

	const isDark =
		customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const fipName = getFipDisplayName(bankObject.fipId, t, bankObject);
	const iconUri =
		bankObject?.productIconUri ||
		global.objectFormatFip[bankObject.fipId]?.productIconUri;
	const isChecked = checkedBanksList?.includes(bankObject.fipId);
	const isMaxReached = checkedBanksList?.length >= 5;

	const handleTooltipClick = () => {
		if (!isTooltipOpen && isMaxReached) setIsTooltipOpen(true);
		else setIsTooltipOpen(!isTooltipOpen);
	};

	const handleCardClick = () => {
		if (isMaxReached && !isChecked) {
			handleTooltipClick();
			return;
		}
		handleOtherFipCheckboxChange(
			{
				target: { checked: !isChecked, name: bankObject.fipId },
			},
			bankObject,
		);
	};

	const card = (
		<Card
			onClick={handleCardClick}
			onKeyDown={handleActivationKey(handleCardClick)}
			role="button"
			tabIndex={0}
			aria-label={fipName}
			sx={{
				...bankCardStyle.card,
				cursor: 'pointer',
				boxShadow: { boxShadow },
				height: { height },
				backgroundColor: isDark ? '#2D2D2D' : '#FFFFFF',
				color: isDark ? '#FFFFFF' : '#000000',
				border: isDark
					? '1px solid #7A8185'
					: '0.0625rem solid var(--storke-stroke-light, #E3E9EE)',
				transition: 'all 0.2s ease-in-out',
				'&:hover': {
					backgroundColor: isDark ? '#3D3D3D' : '#F8F9FA',
					transform: 'translateY(-2px)',
					boxShadow: isDark
						? '0 4px 8px rgba(0,0,0,0.3)'
						: '0 4px 8px rgba(0,0,0,0.1)',
				},
				...keyboardFocusVisibleSx,
			}}
		>
			<ListItem component="div" sx={bankCardStyle.listItem}>
				<Avatar
					src={iconUri}
					alt={getFipLogoAlt(bankObject.fipId, fipName)}
					sx={bankCardStyle.avatar}
				/>
				<ListItemText
					primary={
						<Typography
							noWrap
							sx={{
								...bankCardStyle.bankName,
								...sizeFontCustom.subHeading,
								color: isDark ? '#FFFFFF' : 'inherit',
							}}
						>
							{fipName}
						</Typography>
					}
					sx={{ minWidth: 0, mr: '0.25rem' }}
				/>
				{global?.allowMultipleFipDiscovery === 'true' && (
					<Checkbox
						disableTouchRipple
						disableRipple
						name={bankObject.fipId}
						value={bankObject.fipId}
						disabled={isMaxReached && !isChecked}
						checked={isChecked}
						onChange={(event) =>
							handleOtherFipCheckboxChange(event, bankObject)
						}
						onClick={(event) => event.stopPropagation()}
						inputProps={{ 'aria-label': fipName }}
						sx={{
							paddingRight: '0',
							flexShrink: 0,
							color: isDark ? '#7A8185' : '#D8E1EE',
							'&.Mui-checked': {
								color: customDetails?.primaryColor || '#3053D3',
							},
							'&.Mui-disabled': {
								color: isDark ? '#7A8185' : '#D8E1EE',
							},
							'&:hover': {
								backgroundColor: isDark
									? 'rgba(255,255,255,0.1)'
									: 'rgba(0,0,0,0.04)',
							},
						}}
					/>
				)}
			</ListItem>
		</Card>
	);

	return (
		<Grid item xs={xs} sm={6} md={md} lg={lg} sx={{ pt: '0.94rem!important' }}>
			{isMaxReached && !isChecked ? (
				<Tooltip
					title={t('fipSelection.maxFive')}
					arrow
					open={isTooltipOpen}
					onClose={() => setIsTooltipOpen(false)}
				>
					<Box component="span" sx={{ display: 'block' }}>
						{card}
					</Box>
				</Tooltip>
			) : (
				card
			)}
		</Grid>
	);
}

export default OtherFIUCard;
